import React, { useState, useEffect } from "react";
import {
  Input,
  Form,
  Col,
  Row,
  Button,
  Select,
  Modal,
  Divider,
  InputNumber,
  Tabs,
  DatePicker,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import Config from "../../util/ApiforContext";
import RequestHandler from "util/RequestHandler";
import { defaultValidateMessages } from "./Helper";
import moment from "moment";
import { ColumnHelper } from "../../util/TableHelper";
import { odata } from "../Filters/Helper";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const getForwardInstructionMinDate = () =>
  moment().startOf("day").add(1, "day");
const getForwardInstructionMaxDate = () =>
  moment().startOf("day").add(1, "year");

const BkmPaymentAddForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [bankSelect, setBankSelect] = useState({
    data: [],
    loading: false,
  });
  const [accountSelect, setAccountSelect] = useState({
    data: [],
    loading: false,
  });
  const [redirectModal, setRedirectModal] = useState({
    visible: false,
    redirectUrl: null,
    consentId: null,
    cancelLoading: false,
  });
  const [recipientAccountSelect, setRecipientAccountSelect] = useState({
    data: [],
    loading: false,
  });
  const [activeBank, setActiveBank] = useState();
  const [identityInfo, setIdentityInfo] = useState(null);
  const [disableControl, setDisableControl] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    getBanks();
    getIdentityInfo();
  }, []);

  useEffect(() => {
    if (props.data) {
      setActiveBank(props.data.bankId);
      form.setFieldsValue({
        ...props.data,
        instructionDate: props.data.instructionDate
          ? moment(props.data.instructionDate)
          : undefined,
        returnUrl: Config.frontEndUrl + (props.returnUrl || "payments"),
      });
    }
  }, [form, props.data, props.returnUrl]);

  useEffect(() => {
    const nextValues = {
      returnUrl: Config.frontEndUrl + (props.returnUrl || "payments"),
    };

    if (props.forwardOnly && !props.data?.instructionDate) {
      nextValues.instructionDate = getForwardInstructionMinDate();
    }

    form.setFieldsValue(nextValues);
  }, [form, props.forwardOnly, props.data?.instructionDate, props.returnUrl]);

  useEffect(() => {
    if (!activeBank) return;

    getAccounts(activeBank);
  }, [activeBank]);

  const getBanks = () => {
    setBankSelect({
      ...bankSelect,
      loading: true,
    });
    RequestHandler.get(
      `/Bank?$select=id,name&$filter=isSupportBkm eq true`,
    ).then((response) => {
      setBankSelect({
        data: response.value,
        loading: false,
      });
    });
  };

  const getIdentityInfo = () => {
    RequestHandler.get(`/Tenant/IdentityInfo`, false).then((response) => {
      if (response.success) {
        setIdentityInfo(response.data);
        setDisableControl(false);
      } else {
        setDisableControl(true);
        setIdentityInfo(null);
      }
    });
  };

  const getAccounts = (bankId) => {
    setAccountSelect({
      ...accountSelect,
      loading: true,
    });
    RequestHandler.get(`/BkmSenderAccount?$filter=bankId eq ${bankId}`).then(
      (response) => {
        setAccountSelect({
          data: response.value,
          loading: false,
        });
      },
    );
  };

  const onRecipientAccountSearch = (value) => {
    if (value.length >= 2) {
      setRecipientAccountSelect({ ...recipientAccountSelect, loading: true });

      RequestHandler.get(
        `/BkmRecipientAccount?$select=id,name,ownerName,iban&$filter=(${odata.contains(
          "ownerName",
          value,
        )} or ${odata.contains("name", value)} or ${odata.contains(
          "iban",
          value,
        )})`,
      ).then((res) => {
        setRecipientAccountSelect({ data: res.value, loading: false });
      });
    } else {
      setRecipientAccountSelect({ data: [], loading: false });
    }
  };

  const disabledInstructionDate = (current) => {
    if (!current) return false;

    const currentDate = current.startOf("day");
    const minDate = getForwardInstructionMinDate();
    const maxDate = getForwardInstructionMaxDate();

    return currentDate < minDate || currentDate > maxDate;
  };

  const onFinish = (values) => {
    const requestBody = {
      ...values,
      returnUrl: Config.frontEndUrl + (props.returnUrl || "payments"),
    };

    if (props.forwardOnly && values.instructionDate) {
      requestBody.instructionDate = values.instructionDate.format("YYYY-MM-DD");
    }

    const endpoint = props.forwardOnly
      ? `/BkmForwardPaymentTransaction`
      : `/BkmPaymentTransaction`;

    RequestHandler.post(endpoint, requestBody, false).then((response) => {
      setLoading(false);
      if (response.success) {
        form.resetFields();
        if (props.onSuccess) props.onSuccess();

        setRedirectModal({ ...response.data, visible: true });
      }
    });
  };

  const onFinishFailed = () => setLoading(false);

  const getIdentityType = (type) => {
    const identityTypes = {
      V: "Vergi No",
      K: "T.C. Kimlik No",
      M: "Müşteri No",
      Y: "Yabancı Kimlik No",
      P: "Pasaport No",
    };

    return identityTypes[type];
  };

  const getPaymentPurpose = (type) => {
    const paymentPurposes = {
      "01": "Konut Kirası Ödemesi",
      "02": "İş yeri Kirası Ödemesi",
      "03": "Diğer Kira Ödemesi",
      "04": "E-Ticaret Ödemesi",
      "05": "Çalışan Ödemesi",
      "06": "Ticari ödeme",
      "07": "Bireysel Ödeme",
      "08": "Yatırım",
      "09": "Finansal",
      10: "Eğitim ödemesi",
      11: "Aidat ödemesi",
      12: "Gayrimenkul alım ödemesi",
      13: "Motorlu taşıt alım ödemesi",
      14: "Borç verme/Borç ödeme",
      15: "Hediye/bağış/yardım",
      16: "Vergi/resim/harç ödemesi",
      17: "Tazminat/sigorta ödemesi",
      18: "Avukatlık/Danışmanlık/Müşavirlik Ödemesi",
      19: "Sağlık ödemesi",
      20: "Kripto/dijital varlık",
      21: "Şans oyunları/bahis ödemesi",
      22: "Eğlence/sosyal medya ödemesi",
    };

    return paymentPurposes[type] || type;
  };

  const width = window.innerWidth;
  const breakpoint = 480;
  const textRightClass = width < breakpoint ? "" : "gx-text-right";
  return (
    <>
      <Form
        {...formItemLayout}
        validateMessages={defaultValidateMessages}
        form={form}
        name="bkmBankInfoAdd"
        initialValues={{
          remember: true,
          returnUrl: Config.frontEndUrl + (props.returnUrl || "payments"),
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={(changedValues) => {
          if (!identityInfo || disableControl) return;

          if (changedValues.customerType === "B") {
            form.setFieldsValue({
              senderName: identityInfo.fullName,
            });
          }

          if (changedValues.customerType === "K") {
            form.setFieldsValue({
              senderName: identityInfo.tenantName,
            });
          }
        }}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              hidden
              name="returnUrl"
              initialValue={
                Config.frontEndUrl + (props.returnUrl || "payments")
              }
            >
              <Input />
            </Form.Item>

            <Divider>Gönderen Bilgileri</Divider>

            <Form.Item
              name="bankId"
              label="Hesap Sağlayıcı"
              rules={[{ required: true }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Seçiniz"
                filterOption={(input, option) =>
                  option.children
                    .toLocaleLowerCase("tr-TR")
                    .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                }
                onChange={(value) => setActiveBank(value)}
                loading={bankSelect.loading}
              >
                {bankSelect.data.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="customerType"
              label="Müşteri Türü"
              rules={[{ required: true }]}
            >
              <Select placeholder="Seçiniz">
                <Select.Option key="B" value="B">
                  Bireysel
                </Select.Option>
                <Select.Option key="K" value="K">
                  Kurumsal
                </Select.Option>
              </Select>
            </Form.Item>

            <Tabs defaultActiveKey="0" destroyInactiveTabPane={true}>
              <Tabs.TabPane tab="Iban İle Öde" key="0">
                <Form.Item
                  name="senderName"
                  label="Gönderen Ünvan"
                  rules={[{ required: true }]}
                >
                  <Input disabled={!disableControl} />
                </Form.Item>
                <Form.Item
                  name="senderIban"
                  label="Gönderen Iban"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Kayıtlı Hesap İle Öde" key="1">
                <Form.Item
                  name="bkmSenderAccountId"
                  label="Kayıtlı Hesap"
                  rules={[{ required: true }]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Seçiniz"
                    filterOption={(input, option) =>
                      option.children
                        .toLocaleLowerCase("tr-TR")
                        .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                    }
                    loading={accountSelect.loading}
                  >
                    {accountSelect.data.map((item) => (
                      <Select.Option key={item.id} value={item.id} data={item}>
                        {item.iban + (item.name ? ` (${item.name})` : "")}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Tabs.TabPane>

              <Tabs.TabPane tab="Hesap Sağlayıcıda Seçerek Öde" key="2" />
            </Tabs>

            <Divider>Alıcı Bilgileri</Divider>

            <Tabs defaultActiveKey="0" destroyInactiveTabPane={true}>
              <Tabs.TabPane tab="Iban Gir" key="0">
                <Form.Item
                  name="recipientName"
                  label="Alıcı Ünvan"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="recipientIban"
                  label="Alıcı Iban"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Tabs.TabPane>

              <Tabs.TabPane tab="Kayıtlı Hesap Seç" key="1">
                <Form.Item
                  label="Alıcı Hesap"
                  name="bkmRecipientAccountId"
                  rules={[{ required: true }]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Seçiniz"
                    onSearch={onRecipientAccountSearch}
                    filterOption={(input, option) =>
                      option.props.children
                        .toLocaleLowerCase("tr-TR")
                        .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                    }
                    loading={recipientAccountSelect.loading}
                  >
                    {recipientAccountSelect.data.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {`${item.name} (${item.ownerName} - ${item.iban})`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Tabs.TabPane>
            </Tabs>

            <Divider>Ödeme Bilgileri</Divider>

            {props.forwardOnly && (
              <Form.Item
                name="instructionDate"
                label="Ödeme Talimat Tarihi"
                rules={[
                  {
                    required: true,
                    message: "Lütfen ödeme talimat tarihi seçiniz.",
                  },
                ]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  style={{ width: "100%" }}
                  disabledDate={disabledInstructionDate}
                  placeholder="Tarih seçiniz"
                />
              </Form.Item>
            )}
            <Form.Item
              name="paymentCurrencyCode"
              label="Para Birimi"
              rules={[{ required: true }]}
              initialValue="TRY"
            >
              <Select placeholder="Seçiniz">
                <Select.Option key="TRY" value="TRY">
                  TRY
                </Select.Option>
                <Select.Option key="EUR" value="EUR">
                  EUR
                </Select.Option>
                <Select.Option key="USD" value="USD">
                  USD
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="paymentAmount"
              label="Tutar"
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                precision={2}
                decimalSeparator=","
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              name="paymentPurpose"
              label="Ödeme Amacı"
              rules={[{ required: true }]}
            >
              <Select placeholder="Seçiniz">
                <Select.Option key="01" value="01">
                  Konut Kirası Ödemesi
                </Select.Option>
                <Select.Option key="02" value="02">
                  İş yeri Kirası Ödemesi
                </Select.Option>
                <Select.Option key="03" value="03">
                  Diğer Kira Ödemesi
                </Select.Option>
                <Select.Option key="04" value="04">
                  E-Ticaret Ödemesi
                </Select.Option>
                <Select.Option key="05" value="05">
                  Çalışan Ödemesi
                </Select.Option>
                <Select.Option key="06" value="06">
                  Ticari ödeme
                </Select.Option>
                <Select.Option key="07" value="07">
                  Bireysel Ödeme
                </Select.Option>
                <Select.Option key="08" value="08">
                  Yatırım
                </Select.Option>
                <Select.Option key="09" value="09">
                  Finansal
                </Select.Option>
                <Select.Option key="10" value="10">
                  Eğitim ödemesi
                </Select.Option>
                <Select.Option key="11" value="11">
                  Aidat ödemesi
                </Select.Option>
                <Select.Option key="12" value="12">
                  Gayrimenkul alım ödemesi
                </Select.Option>
                <Select.Option key="13" value="13">
                  Motorlu taşıt alım ödemesi
                </Select.Option>
                <Select.Option key="14" value="14">
                  Borç verme/Borç ödeme
                </Select.Option>
                <Select.Option key="15" value="15">
                  Hediye/bağış/yardım
                </Select.Option>
                <Select.Option key="16" value="16">
                  Vergi/resim/harç ödemesi
                </Select.Option>
                <Select.Option key="17" value="17">
                  Tazminat/sigorta ödemesi
                </Select.Option>
                <Select.Option key="18" value="18">
                  Avukatlık/Danışmanlık/Müşavirlik Ödemesi
                </Select.Option>
                <Select.Option key="19" value="19">
                  Sağlık ödemesi
                </Select.Option>
                <Select.Option key="20" value="20">
                  Kripto/dijital varlık
                </Select.Option>
                <Select.Option key="21" value="21">
                  Şans oyunları/bahis ödemesi
                </Select.Option>
                <Select.Option key="22" value="22">
                  Eğlence/sosyal medya ödemesi
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Açıklama" name="description">
              <Input.TextArea rows={3} maxLength={50} />
            </Form.Item>

            <Col style={{ textAlign: "right" }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                htmlType="submit"
                onClick={() => setLoading(true)}
                loading={loading}
              >
                Gönder
              </Button>
            </Col>
          </Col>
        </Row>
      </Form>

      <Modal
        id="redirectModal"
        title={`${redirectModal.bankName} Rıza Bilgisi`}
        visible={redirectModal.visible}
        onOk={() => {
          setRedirectModal({ visible: false });
          window.location = redirectModal.redirectUrl;
        }}
        okText="Onaylıyorum"
        onCancel={() => {
          setRedirectModal({
            ...redirectModal,
            visible: false,
            cancelLoading: false,
          });
        }}
        width={1000}
        cancelButtonProps={{ loading: redirectModal.cancelLoading }}
        cancelText="İptal Et"
        closable={false}
      >
        <Row style={{ marginBottom: 12 }}>
          <Col span={24}>
            <p style={{ marginBottom: 0, fontWeight: 500 }}>
              Talimatınız alındı.
            </p>
          </Col>
        </Row>

        <Divider>İzin Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.consentId}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza Geçerlilik Tarihi
          </Col>
          <Col xs={24} lg={16}>
            {moment(redirectModal.accessValidityDate).format("DD.MM.YYYY")}
          </Col>
        </Row>

        <Divider>Kimlik Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Müşteri Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.customerType === "K" ? "Kurumsal" : "Bireysel"}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getIdentityType(redirectModal.identityType)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.identityValue}
          </Col>
        </Row>

        {redirectModal.customerType === "K" && (
          <>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik Türü :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {getIdentityType(redirectModal.corporateIdentityType)}
              </Col>
            </Row>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik No :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {redirectModal.corporateIdentityValue}
              </Col>
            </Row>
          </>
        )}

        <Divider>Gönderen Bilgileri</Divider>

        {!(
          redirectModal.senderName ||
          redirectModal.senderIban ||
          redirectModal.senderAccountReference
        ) && (
          <Row>
            <Col span={24}>
              <p>
                <strong>{redirectModal.bankName}</strong> tarafına
                yönlendirildikten sonra seçilecek.
              </p>
            </Col>
          </Row>
        )}

        {redirectModal.senderName && (
          <Row>
            <Col
              xs={24}
              lg={8}
              className={`${textRightClass} gx-font-weight-semi-bold`}
            >
              Gönderen Ünvan :{" "}
            </Col>
            <Col xs={24} lg={16}>
              {redirectModal.senderName}
            </Col>
          </Row>
        )}

        {redirectModal.senderIban && (
          <Row>
            <Col
              xs={24}
              lg={8}
              className={`${textRightClass} gx-font-weight-semi-bold`}
            >
              Gönderen Iban :{" "}
            </Col>
            <Col xs={24} lg={16}>
              {redirectModal.senderIban}
            </Col>
          </Row>
        )}

        {redirectModal.senderAccountReference && (
          <>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Gönderen Hesap Referansı :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {redirectModal.senderAccountReference}
              </Col>
            </Row>
          </>
        )}

        <Divider>Alıcı Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Alıcı Ünvan :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.recipientName}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Alıcı Iban :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.recipientIban}
          </Col>
        </Row>

        <Divider>Ödeme Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Para Birimi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.paymentCurrencyCode}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Tutar :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {ColumnHelper.renderDecimal(redirectModal.paymentAmount)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Referans Bilgisi :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.referenceNumber}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Ödeme Amacı :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getPaymentPurpose(redirectModal.paymentPurpose)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Açıklama :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.description}
          </Col>
        </Row>

        <Divider />

        <Row>
          <Col span={24}>
            <p>
              Yukarıdaki bilgiler ile ödeme işlemi başlatmak için{" "}
              <strong>{redirectModal.bankName}</strong> tarafına
              yönlendirileceksiniz.
            </p>
            <p>İşlemi onaylıyor musunuz?</p>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
export default BkmPaymentAddForm;
